import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

function waitForSwiper(callback) {
  if (typeof Swiper !== 'undefined') {
    callback();
    return;
  }

  let attempts = 0;
  const timer = window.setInterval(() => {
    attempts += 1;
    if (typeof Swiper !== 'undefined' || attempts > 40) {
      window.clearInterval(timer);
      callback();
    }
  }, 100);
}

function getSectionRoot(sectionId) {
  return document.getElementById(`shopify-section-${sectionId}`);
}

function initLazyVideos(root) {
  const videos = root.querySelectorAll('video[data-src]');

  if (!('IntersectionObserver' in window)) {
    videos.forEach((video) => {
      if (video.dataset.src && !video.getAttribute('src')) {
        video.src = video.dataset.src;
        video.removeAttribute('data-src');
      }
    });
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const video = entry.target;
        if (video.dataset.src && !video.getAttribute('src')) {
          video.src = video.dataset.src;
          video.removeAttribute('data-src');
          video.load();
          video.play().catch(() => {});
        }
        observer.unobserve(video);
      });
    },
    { rootMargin: '200px' },
  );

  videos.forEach((video) => observer.observe(video));
}

function findMatchedVariant(product, selectedOptions) {
  return product.variants.find((variant) =>
    selectedOptions.every((value, index) => variant.options[index] === value),
  );
}

function updateVariantUI(scope, product, variant, moneyFormat, currency) {
  const input = scope.querySelector('[data-sv-variant-input]');
  const image = scope.querySelector('[data-sv-product-image]');
  const priceEl = scope.querySelector('[data-sv-product-price]');
  const optionPriceEl = scope.querySelector('[data-sv-option-price]');
  const button = scope.querySelector('[data-sv-add-button]');

  if (input) input.value = variant.id;

  if (image) {
    if (variant.featured_image && variant.featured_image.src) {
      image.src = variant.featured_image.src;
    } else if (product.featured_image) {
      image.src = typeof product.featured_image === 'string' ? product.featured_image : product.featured_image.src;
    }
  }

  const formattedPrice = formatMoney(variant.price, moneyFormat, currency);
  if (priceEl) priceEl.textContent = formattedPrice;
  if (optionPriceEl) optionPriceEl.textContent = formattedPrice;

  if (button) {
    if (!variant.available) {
      button.disabled = true;
      button.textContent = button.dataset.soldOutLabel || 'Sold Out';
    } else {
      button.disabled = false;
      button.textContent = button.dataset.addLabel || button.textContent;
    }
  }
}

function initVariantPickers(root, moneyFormat, currency) {
  root.querySelectorAll('[data-sv-product-json]').forEach((jsonEl) => {
    const blockId = jsonEl.dataset.svProductJson;
    let product;

    try {
      product = JSON.parse(jsonEl.textContent);
    } catch {
      return;
    }

    if (!product || !product.variants) return;

    root.querySelectorAll(`[data-sv-block-id="${blockId}"] [data-sv-variant-select]`).forEach((select) => {
      select.addEventListener('change', () => {
        const context = select.dataset.svContext;
        const scope = root.querySelector(
          `[data-sv-product-scope][data-sv-block-id="${blockId}"][data-sv-context="${context}"]`,
        );
        if (!scope) return;

        const selects = scope.querySelectorAll('[data-sv-variant-select]');
        const selectedOptions = Array.from(selects, (el) => el.value);
        const matchedVariant = findMatchedVariant(product, selectedOptions);
        if (!matchedVariant) return;

        updateVariantUI(scope, product, matchedVariant, moneyFormat, currency);
      });
    });
  });
}

async function handleAddToCart(form, button, sectionId) {
  const addLabel = button.dataset.addLabel || button.textContent;
  const formData = new FormData(form);

  button.disabled = true;

  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: {
        Accept: 'application/json',
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.description || result.message || 'Cart error');
    }

    const cartResponse = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`);
    const cart = await cartResponse.json();

    document.dispatchEvent(
      new CartAddEvent(cart, sectionId, {
        source: 'shopable-video',
        itemCount: cart.item_count,
        variantId: formData.get('id'),
      }),
    );

    button.textContent = button.dataset.addedLabel || 'Added';
    window.setTimeout(() => {
      button.textContent = addLabel;
      button.disabled = false;
    }, 2000);
  } catch {
    button.textContent = addLabel;
    button.disabled = false;
  }
}

function initForms(root, sectionId) {
  root.querySelectorAll('[data-sv-product-form]').forEach((form) => {
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const button = form.querySelector('[data-sv-add-button]');
      if (!button || button.disabled) return;
      handleAddToCart(form, button, sectionId);
    });
  });
}

function initShoppableVideo(sectionId) {
  const root = getSectionRoot(sectionId);
  if (!root) return () => {};

  const config = root.querySelector('[data-shopable-video]');
  if (!config) return () => {};

  const moneyFormat = config.dataset.moneyFormat || '${{amount}}';
  const currency = config.dataset.currency || 'USD';

  const sliders = [];
  const listeners = [];

  const on = (el, eventName, handler) => {
    el.addEventListener(eventName, handler);
    listeners.push(() => el.removeEventListener(eventName, handler));
  };

  const sliderEl = root.querySelector('[data-sv-main-slider]');
  const modalSliderEl = root.querySelector('[data-sv-modal-slider]');
  const modal = root.querySelector('[data-sv-modal]');
  const modalClose = root.querySelector('[data-sv-modal-close]');

  if (sliderEl) {
    sliderEl.classList.remove('preview');

    sliders.push(
      new Swiper(sliderEl, {
        speed: 300,
        breakpoints: {
          320: {
            spaceBetween: parseInt(sliderEl.dataset.gapMobile || '16', 10),
            slidesPerView: parseFloat(sliderEl.dataset.viewMobile || '1.3'),
          },
          768: {
            spaceBetween: parseInt(sliderEl.dataset.gapMobile || '16', 10),
            slidesPerView: parseFloat(sliderEl.dataset.viewMobile || '1.3'),
          },
          1024: {
            spaceBetween: parseInt(sliderEl.dataset.gap || '16', 10),
            slidesPerView: parseFloat(sliderEl.dataset.view || '5'),
          },
        },
      }),
    );
  }

  let modalSwiper = null;

  if (modalSliderEl) {
    modalSwiper = new Swiper(modalSliderEl, {
      speed: 300,
      slidesPerView: 1,
      spaceBetween: 20,
      allowTouchMove: false,
      navigation: {
        nextEl: root.querySelector('[data-sv-modal-next]'),
        prevEl: root.querySelector('[data-sv-modal-prev]'),
      },
      on: {
        slideChange(swiper) {
          swiper.slides.forEach((slide, index) => {
            const video = slide.querySelector('video');
            const muteBtn = slide.querySelector('[data-sv-modal-mute]');

            if (index === swiper.activeIndex) {
              if (video) {
                video.play().catch(() => {});
                video.muted = false;
              }
              if (muteBtn) muteBtn.classList.add('active');
            } else if (video) {
              video.pause();
              video.muted = true;
              if (muteBtn) muteBtn.classList.remove('active');
            }
          });
        },
      },
    });

    sliders.push(modalSwiper);
  }

  root.querySelectorAll('[data-sv-toggle-variants]').forEach((toggle) => {
    on(toggle, 'click', (event) => {
      event.stopPropagation();
      const variants = toggle.closest('.sv__slide')?.querySelector('[data-sv-variants]');
      if (variants) variants.classList.toggle('active');
    });
  });

  root.querySelectorAll('[data-sv-open-modal]').forEach((trigger, index) => {
    on(trigger, 'click', (event) => {
      if (event.target.closest('[data-sv-toggle-variants]') || event.target.closest('form')) return;

      if (!modal || !modalSwiper) return;

      modalSwiper.slideTo(index);
      modal.classList.add('active');
      document.body.style.overflow = 'hidden';

      const activeSlide = modalSwiper.slides[modalSwiper.activeIndex];
      const video = activeSlide?.querySelector('video');
      if (video) {
        if (video.dataset.src && !video.getAttribute('src')) {
          video.src = video.dataset.src;
          video.removeAttribute('data-src');
        }
        video.play().catch(() => {});
        video.muted = false;
      }
    });
  });

  if (modalClose && modal) {
    const closeModal = () => {
      modal.classList.remove('active');
      document.body.style.overflow = '';
      modal.querySelectorAll('video').forEach((video) => video.pause());
    };

    on(modalClose, 'click', closeModal);
    on(modal, 'click', (event) => {
      if (event.target === modal) closeModal();
    });
  }

  root.querySelectorAll('[data-sv-modal-mute]').forEach((btn) => {
    on(btn, 'click', (event) => {
      event.stopPropagation();
      const slide = btn.closest('.sv__modal-slide');
      const video = slide?.querySelector('video');
      if (!video) return;

      btn.classList.toggle('active');
      video.muted = !btn.classList.contains('active');
    });
  });

  initLazyVideos(root);
  initVariantPickers(root, moneyFormat, currency);
  initForms(root, sectionId);

  return () => {
    listeners.forEach((off) => off());
    sliders.forEach((instance) => {
      if (instance && typeof instance.destroy === 'function') {
        instance.destroy(true, true);
      }
    });
  };
}

function bootSection(sectionId) {
  const key = `shopableVideoCleanup:${sectionId}`;
  const priorCleanup = window[key];
  if (typeof priorCleanup === 'function') priorCleanup();

  waitForSwiper(() => {
    window[key] = initShoppableVideo(sectionId);
  });
}

document.addEventListener('shopify:section:load', (event) => {
  const sectionId = event?.detail?.sectionId;
  if (!sectionId) return;
  const root = getSectionRoot(sectionId);
  if (root?.querySelector('[data-shopable-video]')) bootSection(sectionId);
});

document.addEventListener('shopify:section:unload', (event) => {
  const sectionId = event?.detail?.sectionId;
  if (!sectionId) return;
  const key = `shopableVideoCleanup:${sectionId}`;
  const cleanup = window[key];
  if (typeof cleanup === 'function') cleanup();
  window[key] = null;
});

document.querySelectorAll('[data-shopable-video][data-section-id]').forEach((el) => {
  bootSection(el.getAttribute('data-section-id'));
});
